import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  fetchSocialComments,
  createSocialComment,
  deleteSocialComment,
  reactToSocialComment,
} from "./communitySocialApi";
import { CommentBodyWithMedia } from "./youtubeEmbed";
import PrayerTypeDialog from "./PrayerTypeDialog";
import ReactionTypeDialog from "./ReactionTypeDialog";

const PREVIEW_COUNT = 2;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Inline comment thread for a Community post.
 * Shows first two comments by default; expands/collapses the rest.
 * Like / Pray reuse the same Community reaction dialogs as posts.
 */
function CommunityPostComments({
  postId,
  commentsLocked = false,
  initialCount = 0,
  onCountChange = null,
}) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(!!commentsLocked);
  const [reactTargetId, setReactTargetId] = useState(null);
  const [prayTargetId, setPrayTargetId] = useState(null);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    setError(false);
    const result = await fetchSocialComments(postId);
    setLoading(false);
    if (result.error) {
      setComments([]);
      setError(true);
      return;
    }
    setComments(result.comments || []);
    setLocked(!!result.comments_locked);
    if (typeof onCountChange === "function") {
      onCountChange(
        typeof result.comment_count === "number"
          ? result.comment_count
          : (result.comments || []).length
      );
    }
    // onCountChange intentionally omitted from deps (parent inline handler).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = expanded ? comments : comments.slice(0, PREVIEW_COUNT);
  const hiddenCount = Math.max(0, comments.length - PREVIEW_COUNT);

  const patchComment = (updated) => {
    if (!updated || !updated.id) return;
    setComments((prev) =>
      prev.map((c) => (Number(c.id) === Number(updated.id) ? { ...c, ...updated } : c))
    );
  };

  const clearMedia = () => {
    setMediaFile(null);
    if (mediaPreview) {
      try {
        URL.revokeObjectURL(mediaPreview);
      } catch (e) {
        /* ignore */
      }
    }
    setMediaPreview("");
  };

  const onPickMedia = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (IMAGE_TYPES.indexOf(file.type) < 0) {
      setFormError("Comments support one image (JPG, PNG, GIF, or WebP).");
      return;
    }
    setFormError("");
    clearMedia();
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const body = draft.trim();
    if ((!body && !mediaFile) || busy || locked) return;
    setBusy(true);
    setFormError("");
    const result = await createSocialComment(postId, body, mediaFile);
    setBusy(false);
    if (result.error) {
      setFormError(result.message || "Unable to comment");
      return;
    }
    setDraft("");
    clearMedia();
    if (result.comment) {
      setComments((prev) => [...prev, result.comment]);
    } else {
      await load();
    }
    if (typeof onCountChange === "function") {
      onCountChange(
        typeof result.comment_count === "number"
          ? result.comment_count
          : initialCount + 1
      );
    }
  };

  const remove = async (commentId) => {
    if (busy) return;
    setBusy(true);
    const result = await deleteSocialComment(commentId);
    setBusy(false);
    if (result.error) return;
    setComments((prev) => prev.filter((c) => Number(c.id) !== Number(commentId)));
    if (typeof onCountChange === "function") {
      onCountChange(
        typeof result.comment_count === "number"
          ? result.comment_count
          : Math.max(0, initialCount - 1)
      );
    }
  };

  const runReaction = async (
    commentId,
    reaction,
    prayerTypeId = null,
    reactionTypeId = null
  ) => {
    setBusy(true);
    const result = await reactToSocialComment(
      commentId,
      reaction,
      prayerTypeId,
      reactionTypeId
    );
    setBusy(false);
    if (!result.error && result.comment) {
      patchComment(result.comment);
    }
  };

  const reactComment = comments.find(
    (c) => Number(c.id) === Number(reactTargetId)
  );
  const prayComment = comments.find(
    (c) => Number(c.id) === Number(prayTargetId)
  );

  return (
    <div className="community-post__comments">
      {loading ? (
        <p className="community-post__comments-status" role="status">
          Loading comments…
        </p>
      ) : error ? (
        <p className="community-post__comments-status" role="alert">
          Couldn’t load comments.{" "}
          <button
            type="button"
            className="community-post__comments-link"
            onClick={() => load()}
          >
            Retry
          </button>
        </p>
      ) : null}

      {!loading && !error && comments.length === 0 ? (
        <p className="community-post__comments-status">
          Be the first to comment.
        </p>
      ) : null}

      <ul className="community-post__comment-list">
        {visible.map((c) => {
          const author = c.author || {};
          const name = author.display_name || author.name || "Member";
          const avatar = author.avatar;
          const path = author.path;
          const counts = c.counts || {};
          const viewerReactions = c.viewer_reactions || [];
          const hasLike =
            viewerReactions.indexOf("like") >= 0 ||
            c.viewer_reaction_type_id != null;
          const hasPray =
            viewerReactions.indexOf("prayer") >= 0 ||
            viewerReactions.indexOf("amen") >= 0;
          return (
            <li key={c.id} className="community-post__comment">
              <span className="community-post__comment-avatar" aria-hidden="true">
                {avatar ? (
                  <img
                    src={resolveMediaUrl(avatar)}
                    alt=""
                    onError={handleMediaImgError}
                  />
                ) : (
                  (name || "?").charAt(0).toUpperCase()
                )}
              </span>
              <div className="community-post__comment-body">
                <div className="community-post__comment-meta">
                  {path ? (
                    <Link to={path} className="community-post__comment-name">
                      {name}
                    </Link>
                  ) : (
                    <span className="community-post__comment-name">{name}</span>
                  )}
                  {c.timestamp ? (
                    <span className="community-post__comment-time">{c.timestamp}</span>
                  ) : null}
                </div>
                <CommentBodyWithMedia
                  text={c.body}
                  media={
                    c.media && c.media.path
                      ? {
                          ...c.media,
                          path: resolveMediaUrl(c.media.path),
                        }
                      : null
                  }
                  embed={c.embed || null}
                  linkPreview={c.link_preview || null}
                />
                <div className="community-post__comment-actions">
                  <button
                    type="button"
                    className={
                      "community-post__comment-action" +
                      (hasLike ? " is-active" : "")
                    }
                    onClick={() => setReactTargetId(c.id)}
                    disabled={busy}
                  >
                    Like{counts.like ? ` · ${counts.like}` : ""}
                  </button>
                  <button
                    type="button"
                    className={
                      "community-post__comment-action" +
                      (hasPray ? " is-active" : "")
                    }
                    onClick={() => setPrayTargetId(c.id)}
                    disabled={busy}
                  >
                    Pray{counts.prayer ? ` · ${counts.prayer}` : ""}
                  </button>
                  {c.is_owner ? (
                    <button
                      type="button"
                      className="community-post__comment-delete"
                      onClick={() => remove(c.id)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {hiddenCount > 0 && !expanded ? (
        <button
          type="button"
          className="community-post__comments-toggle"
          onClick={() => setExpanded(true)}
        >
          {comments.length > PREVIEW_COUNT
            ? `View all ${comments.length} comments`
            : "View more comments"}
        </button>
      ) : null}

      {expanded && comments.length > PREVIEW_COUNT ? (
        <button
          type="button"
          className="community-post__comments-toggle"
          onClick={() => setExpanded(false)}
        >
          Hide comments
        </button>
      ) : null}

      {locked ? (
        <p className="community-post__comments-status">
          Comments are locked on this post.
        </p>
      ) : (
        <form className="community-post__comment-form" onSubmit={submit}>
          <label htmlFor={`community-comment-${postId}`} className="sr-only">
            Write a comment
          </label>
          <textarea
            id={`community-comment-${postId}`}
            className="community-post__comment-input"
            rows={2}
            maxLength={2000}
            placeholder="Write a comment… Paste a YouTube or Vimeo link for one video."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
          />
          {mediaPreview ? (
            <div className="community-post__comment-preview">
              <img src={mediaPreview} alt="" />
              <button
                type="button"
                className="community-post__comment-preview-remove"
                onClick={clearMedia}
                disabled={busy}
              >
                Remove image
              </button>
            </div>
          ) : null}
          {formError ? (
            <p className="community-post__comments-status" role="alert">
              {formError}
            </p>
          ) : (
            <p className="community-post__comments-hint">
              One image or one YouTube/Vimeo link — not both.
            </p>
          )}
          <div className="community-post__comment-form-actions">
            <label className="community-post__action community-post__comment-attach">
              Image
              <input
                type="file"
                accept={IMAGE_TYPES.join(",")}
                className="sr-only"
                onChange={onPickMedia}
                disabled={busy}
              />
            </label>
            <button
              type="submit"
              className="community-post__action community-post__comment-submit"
              disabled={busy || (!draft.trim() && !mediaFile)}
            >
              {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>
      )}

      <ReactionTypeDialog
        open={reactTargetId != null}
        busy={busy}
        activeTypeId={
          (reactComment && reactComment.viewer_reaction_type_id) || null
        }
        hasActiveReaction={
          !!(
            reactComment &&
            ((reactComment.viewer_reactions || []).indexOf("like") >= 0 ||
              reactComment.viewer_reaction_type_id != null)
          )
        }
        onClose={() => setReactTargetId(null)}
        onSelect={async (type) => {
          if (!reactTargetId) return;
          if (!type) {
            await runReaction(reactTargetId, "like");
          } else {
            await runReaction(reactTargetId, "like", null, type.id);
          }
          setReactTargetId(null);
        }}
      />
      <PrayerTypeDialog
        open={prayTargetId != null}
        busy={busy}
        activeTypeId={(prayComment && prayComment.viewer_prayer_type_id) || null}
        onClose={() => setPrayTargetId(null)}
        onSelect={async (type) => {
          if (!prayTargetId) return;
          if (!type) {
            await runReaction(prayTargetId, "prayer");
          } else {
            await runReaction(prayTargetId, "prayer", type.id);
          }
          setPrayTargetId(null);
        }}
      />
    </div>
  );
}

export default CommunityPostComments;
